import React, { useState, useEffect } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
    Alert,
    Box,
} from '@mui/material';
import { Payment } from '../../../models/Payment';
import { getAllPayments } from '../../../services/paymentService';
import { formatCurrency } from '../../../utils/formatters';

interface ClientPaymentsProps {
    clientId: string;
}

const ClientPayments: React.FC<ClientPaymentsProps> = ({ clientId }) => {
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchPayments();
    }, [clientId]);

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const allPayments = await getAllPayments();
            const clientPayments = allPayments.filter(payment => payment.clientId === clientId);
            setPayments(clientPayments);
            setError(null);
        } catch (err) {
            setError('Failed to fetch payments');
            console.error('Error fetching payments:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Payments</Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Cost</TableCell>
                            <TableCell>Due Date</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Order ID</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {payments.map((payment) => (
                            <TableRow key={payment.id}>
                                <TableCell>{payment.name || '-'}</TableCell>
                                <TableCell>{payment.type}</TableCell>
                                <TableCell>{formatCurrency(payment.amount)}</TableCell>
                                <TableCell>{formatCurrency(payment.cost)}</TableCell>
                                <TableCell>{payment.dueDate ? payment.dueDate.toLocaleDateString() : '-'}</TableCell>
                                <TableCell>{payment.status}</TableCell>
                                <TableCell>{payment.orderId || '-'}</TableCell>
                            </TableRow>
                        ))}
                        {payments.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No payments found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default ClientPayments; 